import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { agentApi, conversationApi, knowledgeApi, approvalApi } from '@/lib/api';
import {
  Bot, MessageSquare, BookOpen, CheckSquare, Clock,
  CheckCircle, AlertTriangle, BarChart3, Activity,
} from 'lucide-react';
import { Link } from '@tanstack/react-router';
import styles from '../pages/DashboardPage.module.css';
import { PageGuide } from '@/components/PageGuide';


export const Route = createFileRoute('/_auth/dashboard')({
  component: DashboardPage,
});

function StatCard({
  icon: Icon,
  label,
  value,
  sub,
  color,
  to,
}: {
  icon: typeof Bot;
  label: string;
  value: string | number;
  sub?: string;
  color?: string;
  to?: string;
}) {
  const inner = (
    <div className={styles.statCard} style={{ '--stat-color': color } as React.CSSProperties}>
      <div className={styles.statIcon}><Icon size={20} /></div>
      <div className={styles.statBody}>
        <div className={styles.statValue}>{value}</div>
        <div className={styles.statLabel}>{label}</div>
        {sub && <div className={styles.statSub}>{sub}</div>}
      </div>
    </div>
  );
  return to ? <Link to={to} className={styles.statLink}>{inner}</Link> : inner;
}

export default function DashboardPage() {
  const { platformUser } = useAuth();
  const ready = !!platformUser;

  const { data: agents = [] } = useQuery({ queryKey: ['agents'], queryFn: agentApi.list, enabled: ready, retry: false });
  const { data: conversations = [] } = useQuery({ queryKey: ['conversations'], queryFn: () => conversationApi.list(), enabled: ready, retry: false });
  const { data: sources = [] } = useQuery({ queryKey: ['knowledge-sources'], queryFn: knowledgeApi.listSources, enabled: ready, retry: false });
  const { data: pendingApprovals = [] } = useQuery({ queryKey: ['approvals', 'PENDING'], queryFn: () => approvalApi.list('PENDING'), enabled: ready, retry: false });

  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    return 'Good evening';
  })();

  const agentArr = agents;
  const convArr = conversations as Array<{ id: string; status: string; createdAt: string }>;
  const sourceArr = sources as Array<{ id: string; name: string; _count?: { articles: number } }>;
  const approvalArr = pendingApprovals as Array<{ id: string }>;

  const activeAgents = agentArr.filter(a => a.active).length;
  const openConversations = convArr.filter(c => c.status === 'ACTIVE' || c.status === 'OPEN').length;
  const totalArticles = sourceArr.reduce((sum, s) => sum + (s._count?.articles ?? 0), 0);

  return (
    <div className={styles.page}>
      <PageGuide pageKey="dashboard" />
      {/* ── Header ── */}
      <div className={styles.header}>
        <div>
          <h1 className={styles.greeting}>
            {greeting}, {platformUser?.name?.split(' ')[0] ?? 'there'} 👋
          </h1>
          <p className={styles.sub}>
            <strong>{platformUser?.tenant.name}</strong> · {platformUser?.role}
          </p>
        </div>
        <div className={styles.planBadge}>
          <Activity size={14} />
          {platformUser?.tenant.plan} plan · <span className={styles.statusDot} /> Active
        </div>
      </div>

      {/* ── Stats row ── */}
      <div className={styles.statsGrid}>
        <StatCard icon={Bot} label="Agents" value={agentArr.length} sub={`${activeAgents} active`} color="#6366f1" to="/agents" />
        <StatCard icon={MessageSquare} label="Conversations" value={convArr.length} sub={`${openConversations} open`} color="#10b981" to="/conversations" />
        <StatCard icon={BookOpen} label="Knowledge articles" value={totalArticles} sub={`${sourceArr.length} sources`} color="#f59e0b" to="/knowledge" />
        <StatCard icon={CheckSquare} label="Pending approvals" value={approvalArr.length} sub="require action" color={approvalArr.length > 0 ? '#ef4444' : '#6b7280'} to="/approvals" />
      </div>

      {/* ── Quick actions ── */}
      <h2 className={styles.sectionTitle}>Quick actions</h2>
      <div className={styles.actionsGrid}>
        <Link to="/agents/new" className={styles.actionCard}>
          <Bot size={22} className={styles.actionIcon} />
          <div>
            <div className={styles.actionTitle}>New Agent</div>
            <div className={styles.actionDesc}>Create a new AI agent with custom skills and prompt</div>
          </div>
        </Link>
        <Link to="/knowledge" className={styles.actionCard}>
          <BookOpen size={22} className={styles.actionIcon} />
          <div>
            <div className={styles.actionTitle}>Add Knowledge</div>
            <div className={styles.actionDesc}>Create a knowledge source and add articles for agents to use</div>
          </div>
        </Link>
        <Link to="/conversations" className={styles.actionCard}>
          <BarChart3 size={22} className={styles.actionIcon} />
          <div>
            <div className={styles.actionTitle}>View Conversations</div>
            <div className={styles.actionDesc}>Monitor active conversations and view transcripts</div>
          </div>
        </Link>
        {approvalArr.length > 0 && (
          <Link to="/approvals" className={`${styles.actionCard} ${styles.urgent}`}>
            <AlertTriangle size={22} className={styles.actionIcon} />
            <div>
              <div className={styles.actionTitle}>{approvalArr.length} Pending Approvals</div>
              <div className={styles.actionDesc}>Agents are waiting for your approval to proceed</div>
            </div>
          </Link>
        )}
      </div>

      {/* ── Recent conversations ── */}
      {convArr.length > 0 && (
        <>
          <h2 className={styles.sectionTitle}>Recent conversations</h2>
          <div className={styles.convList}>
            {convArr.slice(0, 5).map(conv => (
              <Link key={conv.id} to="/conversations/$conversationId" params={{ conversationId: conv.id }} className={styles.convRow}>
                <div className={styles.convStatus}>
                  {conv.status === 'ACTIVE' || conv.status === 'OPEN'
                    ? <CheckCircle size={14} className={styles.activeIcon} />
                    : <Clock size={14} className={styles.closedIcon} />}
                </div>
                <div className={styles.convId}>{conv.id.slice(0, 8)}…</div>
                <div className={`${styles.convBadge} ${conv.status === 'ACTIVE' || conv.status === 'OPEN' ? styles.open : styles.closed}`}>
                  {conv.status}
                </div>
                <div className={styles.convTime}>{new Date(conv.createdAt).toLocaleString()}</div>
              </Link>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
